/**
 * A Team Work session, read and joined from the console (people in Team Work, workbench 0.89.20). The messages as
 * the team and its people wrote them; a reply, or a steer for the session's owner and steerers; the questions an
 * agent asked a named person, answered in place by that person or by whoever covers them under the team's
 * delegation rules; @ mentions written the way the workbench reads them. `?message=<id>` opens at that message
 * (the inbox's Open).
 */

import { mentionsIn, teamPeople, type ConversationRecord, type ProjectMember, type TeamDelegation, type TeamworkIntent } from '@aioe/shared';
import { ArrowDown, At, CircleQuestion, Comments, Cpu, Gear, Person, Persons } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ApiError, routes, type ProjectTeamDocument } from '../../api/client.ts';
import { useAuth } from '../../auth/context.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Markdown } from '../Markdown.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, Page, Stat, StatusMark } from '../ui.tsx';
import { VIEW_TONES, ViewHeader } from '../ui/Art.tsx';
import { MentionComposer, type PickablePerson } from './MentionComposer.tsx';
import { answeredWords, authorOf, firstLine, mayAnswer, maySteer, mentionsMe, namesWords, newMessageId, questionsIn, refusalWords, type Question, type SessionEntry } from './teamworkModel.ts';

const NONE = '__none';
const tone = VIEW_TONES.projects!;

type TeamDoc = ProjectTeamDocument & { delegation?: TeamDelegation; owner?: { id: string; name?: string } };

/** Every message of the session, page by page (500 at a time). */
async function allMessages(projectId: string, cid: string): Promise<SessionEntry[]> {
  const out: SessionEntry[] = [];
  let after = 0;
  for (let i = 0; i < 20; i++) {
    const page = await routes.teamworkMessages(projectId, cid, after);
    out.push(...(page.entries as SessionEntry[]));
    if (!page.more || !page.entries.length) break;
    after = page.entries[page.entries.length - 1]!.seq;
  }
  return out;
}

export function TeamworkSessionPage() {
  const { projectId = '', cid = '' } = useParams();
  const [params] = useSearchParams();
  const { account } = useAuth();
  const me = account?.id;
  const conv = useLoad(() => routes.teamworkConversation(projectId, cid), [projectId, cid], 30_000);
  const msgs = useLoad(() => allMessages(projectId, cid), [projectId, cid], 8_000);
  const project = useLoad(() => routes.project(projectId), [projectId]);
  const teams = useLoad(() => routes.projectTeamsV2(projectId).catch(() => [] as ProjectTeamDocument[]), [projectId]);
  const rec = conv.data;
  const team = (teams.data ?? []).find((t) => t.id === rec?.data.teamId) as TeamDoc | undefined;
  const delegation = team?.delegation;
  const entries = msgs.data ?? [];
  const questions = questionsIn(entries);

  // Who is who: the project's members, the team's people, and every author the session has seen.
  const members = (project.data?.members ?? []).filter((m: ProjectMember) => m.subjectType === 'user');
  const names = (() => {
    const n = new Map<string, string>();
    for (const m of members) n.set(m.subjectId, m.name ?? m.email ?? 'Someone on the project');
    for (const m of team?.members ?? []) if (m.human && m.subjectId && !n.has(m.subjectId)) n.set(m.subjectId, m.name);
    for (const e of entries) if (e.data.author?.kind === 'person' && e.data.author.id) n.set(e.data.author.id, e.data.author.name);
    if (rec?.data.owner) n.set(rec.data.owner.id, rec.data.owner.name);
    return n;
  })();
  const nameOf = (id: string) => (id === me ? 'you' : (names.get(id) ?? 'someone on the project'));

  // Whether the people a question names are away, for the cover rules that apply only then.
  const coveredTo = [...new Set(questions.filter((q) => !q.answer && q.ask.to !== me && delegation?.cover.some((c) => c.person === q.ask.to && c.when === 'away' && me && c.by.includes(me))).map((q) => q.ask.to))];
  const avail = useLoad(() => (coveredTo.length ? routes.availability(coveredTo) : Promise.resolve({ people: [] })), [coveredTo.join(',')]);
  const away = new Set((avail.data?.people ?? []).filter((p) => p.away).map((p) => p.id));

  const [known, setKnown] = useState<PickablePerson[]>([]);
  const pickable = (() => {
    const out: PickablePerson[] = [];
    const seen = new Set<string>();
    const add = (p: PickablePerson) => {
      if (seen.has(p.subjectId) || p.subjectId === me) return;
      seen.add(p.subjectId);
      out.push(p);
    };
    if (team) for (const id of teamPeople({ members: team.members, ...(team.owner ? { owner: team.owner } : {}) })) add({ subjectId: id, name: names.get(id) ?? id, where: 'team', onProject: members.some((m) => m.subjectId === id) });
    for (const m of members) add({ subjectId: m.subjectId, name: m.name ?? m.email ?? m.subjectId, where: 'project', onProject: true });
    for (const k of known) add(k);
    return out.filter((p) => p.name !== p.subjectId || p.where !== 'team');
  })();

  const [filter, setFilter] = useState<'all' | 'mentions'>('all');
  const mine = entries.filter((e) => mentionsMe(e.data, me));
  const waiting = questions.filter((q) => mayAnswer(q, me, delegation, away));
  const shown = filter === 'mentions' ? mine : entries;
  const steer = maySteer(rec, me);

  // `?message=` (the inbox's Open), then "Next mention": scroll to a message and mark it for a moment.
  const [flash, setFlash] = useState<string>();
  const jumped = useRef(false);
  const scrollTo = (id: string) => {
    const el = document.getElementById(`tw-${id}`);
    if (!el) return false;
    el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    setFlash(id);
    window.setTimeout(() => setFlash((f) => (f === id ? undefined : f)), 2500);
    return true;
  };
  const wanted = params.get('message');
  useEffect(() => {
    if (!wanted || jumped.current || !entries.length) return;
    jumped.current = scrollTo(wanted);
  }, [wanted, entries.length]);
  const nextMention = () => {
    if (!mine.length) return;
    const fold = window.innerHeight / 2;
    const next = mine.find((e) => (document.getElementById(`tw-${e.id}`)?.getBoundingClientRect().top ?? 0) > fold + 4) ?? mine[0]!;
    scrollTo(next.id);
  };

  const title = rec?.data.title || 'Team Work session';
  const notFound = conv.error && /no longer there/i.test(conv.error);
  return (
    <Page>
      <ViewHeader
        tone={tone}
        icon={<Comments />}
        title={rec ? title : conv.loading ? 'Loading session…' : 'Team Work session'}
        blurb={rec ? `${project.data?.project.name ?? 'Project'} · ${team ? `${team.name} · ` : ''}${rec.data.owner ? `started by ${nameOf(rec.data.owner.id)}` : 'Team Work'} · updated ${timeAgo(rec.updatedAt)}` : undefined}
        actions={
          <Link to={`/projects/${encodeURIComponent(projectId)}?tab=work&part=teamwork`} className="text-sm text-muted hover:text-foreground">
            All sessions
          </Link>
        }
      />
      {notFound ? (
        <Empty tone={tone} icon={<Comments />} title="This session is no longer here" body="It was removed, or it is in a project you are not on." />
      ) : (
        <ErrorNote error={conv.error} />
      )}
      {rec && (
        <>
          <div className="art-stats">
            <Stat tone={tone} icon={<Comments />} label="Messages" value={entries.length} detail={entries.length ? `last ${timeAgo(entries[entries.length - 1]!.at)}` : 'nothing yet'} />
            <Stat tone={tone} icon={<CircleQuestion />} label="Waiting on you" value={waiting.length} detail={waiting.length ? 'questions you can answer' : 'no questions for you'} needsYou={waiting.length > 0} />
            <Stat tone={tone} icon={<At />} label="Mentions of you" value={mine.length} detail={mine.length ? 'messages that name you' : 'nobody named you yet'} />
          </div>
          <ListGroup
            title="Conversation"
            count={shown.length}
            actions={
              <>
                <Segment aria-label="Which messages" size="sm" selectedKey={filter} onSelectionChange={(k) => setFilter(String(k) as 'all' | 'mentions')} className="w-fit">
                  <Segment.Item id="all">All</Segment.Item>
                  <Segment.Item id="mentions">Mentions</Segment.Item>
                </Segment>
                <Button size="sm" variant="ghost" isDisabled={!mine.length} onPress={nextMention}>
                  Next mention <ArrowDown className="size-3.5" aria-hidden />
                </Button>
              </>
            }
            empty={msgs.data && !shown.length && !msgs.error ? <Empty compact tone={tone} icon={filter === 'mentions' ? <At /> : <Comments />} title={filter === 'mentions' ? 'Nobody has mentioned you here' : 'Nothing said yet'} body={filter === 'mentions' ? 'When someone writes @ and your name, the message shows here.' : 'The team writes here as it works; you can add a reply below.'} /> : undefined}
          >
            {msgs.error ? (
              <ListBlock>
                <ErrorNote error={msgs.error} />
              </ListBlock>
            ) : null}
            {!msgs.data && !msgs.error ? <ListBlock className="text-sm text-muted">Reading the session…</ListBlock> : null}
            {shown.length ? (
              <ListBlock flush>
                <ol className="m-0 list-none divide-y divide-separator p-0">
                  {shown.map((e) => (
                    <MessageRow
                      key={e.id}
                      e={e}
                      me={me}
                      flash={flash === e.id}
                      question={e.data.asks ? questions.find((q) => q.ask.questionId === e.data.asks!.questionId) : undefined}
                      canAnswer={(q) => mayAnswer(q, me, delegation, away)}
                      nameOf={nameOf}
                      projectId={projectId}
                      cid={cid}
                      onPosted={msgs.reload}
                    />
                  ))}
                </ol>
              </ListBlock>
            ) : null}
          </ListGroup>
          <Composer projectId={projectId} cid={cid} rec={rec} steer={steer} people={pickable} nameOf={nameOf} onKnown={(p) => setKnown((k) => [...k.filter((x) => x.subjectId !== p.subjectId), p])} onPosted={msgs.reload} />
          <Steerers projectId={projectId} rec={rec} me={me} members={members} nameOf={nameOf} onChange={conv.reload} />
        </>
      )}
    </Page>
  );
}

const KIND_ICON = { person: <Person />, agent: <Cpu />, system: <Gear /> } as const;
const KIND_WORDS = { person: 'Person', agent: 'Agent', system: 'Workbench' } as const;

function MessageRow({ e, me, flash, question, canAnswer, nameOf, projectId, cid, onPosted }: { e: SessionEntry; me?: string; flash: boolean; question?: Question; canAnswer: (q: Question) => boolean; nameOf: (id: string) => string; projectId: string; cid: string; onPosted: () => void }) {
  const m = e.data;
  const a = authorOf(m);
  const named = mentionsMe(m, me);
  const meta = [KIND_WORDS[a.kind], timeAgo(m.createdAt || e.at), m.intent === 'steer' ? 'steering the team' : undefined, m.intent === 'answer' && m.answeredFor ? `answering for ${m.answeredFor.name}` : m.intent === 'answer' ? 'an answer' : undefined, named ? 'mentions you' : undefined].filter(Boolean).join(' · ');
  return (
    <li id={`tw-${m.id}`} className={`grid gap-1.5 px-4 py-3 transition-colors ${flash ? 'bg-accent/10' : ''} ${named ? 'border-l-2 border-l-accent' : ''}`}>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="relative top-0.5 shrink-0 text-muted [&>svg]:size-3.5" aria-hidden>
          {KIND_ICON[a.kind]}
        </span>
        <span className="min-w-0 break-words text-sm font-medium">{a.id === me && a.kind === 'person' ? 'You' : a.name}</span>
        <span className="min-w-0 text-xs text-muted">{meta}</span>
      </div>
      {m.text ? <Markdown text={m.text} compact breaks className="text-sm" /> : null}
      {question ? <QuestionBlock q={question} me={me} canAnswer={canAnswer(question)} nameOf={nameOf} projectId={projectId} cid={cid} onPosted={onPosted} /> : null}
    </li>
  );
}

function QuestionBlock({ q, me, canAnswer, nameOf, projectId, cid, onPosted }: { q: Question; me?: string; canAnswer: boolean; nameOf: (id: string) => string; projectId: string; cid: string; onPosted: () => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const forMe = q.ask.to === me;
  const answer = async (value: string) => {
    if (!value.trim()) return;
    setBusy(value);
    setError(undefined);
    try {
      await routes.postTeamworkMessage(projectId, cid, { id: newMessageId(), role: 'user', text: value.trim(), createdAt: new Date().toISOString(), intent: 'answer', questionId: q.ask.questionId });
      setText('');
      onPosted();
    } catch (e) {
      setError(e instanceof ApiError ? (refusalWords(e.body, nameOf) ?? e.message) : (e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <div className="grid gap-2 border-l-2 border-separator pl-3">
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 text-sm font-medium">{q.ask.question}</span>
        {q.answer ? <StatusMark wrap state="online" label={answeredWords(q)} /> : <StatusMark wrap state="idle" label={forMe ? 'Waiting for you' : `Waiting for ${nameOf(q.ask.to)}`} />}
      </div>
      <span className="text-xs text-muted">
        {`${q.askedBy} asked ${forMe ? 'you' : nameOf(q.ask.to)}`}
        {q.ask.blocking ? ' · the work waits on the answer' : ' · the work carries on meanwhile'}
        {!forMe && canAnswer ? ' · you cover them' : ''}
      </span>
      {q.answer ? <Markdown text={firstLine(q.answer.text, 400)} compact className="text-sm text-muted" /> : null}
      {!q.answer && canAnswer ? (
        q.ask.options?.length ? (
          <div className="flex flex-wrap gap-2">
            {q.ask.options.map((o) => (
              <Button key={o} size="sm" variant="secondary" isDisabled={Boolean(busy)} onPress={() => void answer(o)}>
                {busy === o ? `${o}…` : o}
              </Button>
            ))}
          </div>
        ) : (
          <div className="grid gap-2">
            <textarea aria-label={`Your answer to ${q.ask.question}`} className="w-full resize-y rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-sm outline-none focus:border-accent" rows={2} value={text} onChange={(ev) => setText(ev.target.value)} placeholder="Your answer" />
            <div>
              <Button size="sm" isDisabled={!text.trim() || Boolean(busy)} onPress={() => void answer(text)}>
                {busy ? 'Answering…' : forMe ? 'Answer' : `Answer for ${nameOf(q.ask.to)}`}
              </Button>
            </div>
          </div>
        )
      ) : null}
      <ErrorNote error={error} />
    </div>
  );
}

function Composer({ projectId, cid, rec, steer, people, nameOf, onKnown, onPosted }: { projectId: string; cid: string; rec: ConversationRecord; steer: boolean; people: PickablePerson[]; nameOf: (id: string) => string; onKnown: (p: PickablePerson) => void; onPosted: () => void }) {
  const [text, setText] = useState('');
  const [intent, setIntent] = useState<TeamworkIntent>('reply');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const mentions = mentionsIn(text, people.filter((p) => p.onProject));
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await routes.postTeamworkMessage(projectId, cid, { id: newMessageId(), role: 'user', text: text.trim(), createdAt: new Date().toISOString(), intent: steer ? intent : 'reply', ...(mentions.length ? { mentions } : {}) });
      setText('');
      onPosted();
    } catch (e) {
      setError(e instanceof ApiError ? (refusalWords(e.body, nameOf) ?? e.message) : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const steerers = [rec.data.owner?.id, ...(rec.data.steerers ?? [])].filter((x): x is string => Boolean(x));
  const hint = steer ? (intent === 'steer' ? 'A steer tells the team what to do next.' : 'A reply joins the conversation; the team reads it but is not redirected.') : steerers.length ? `${namesWords(steerers.map(nameOf))} ${steerers.length === 1 ? 'steers' : 'steer'} this session; you can reply.` : undefined;
  return (
    <ListGroup title={steer && intent === 'steer' ? 'Steer the team' : 'Reply'} description={hint}>
      <ListBlock className="grid gap-3">
        <MentionComposer value={text} onChange={setText} people={people} projectId={projectId} onKnown={onKnown} label="Your message" placeholder="Write a message. Type @ to mention someone." onSubmit={() => void send()} isDisabled={busy} />
        {mentions.length ? <span className="text-xs text-muted">Tells {namesWords(mentions.map((x) => x.name))}.</span> : null}
        <ErrorNote error={error} />
        <div className="flex flex-wrap items-center justify-end gap-2">
          {steer ? (
            <Segment aria-label="What the message is for" size="sm" selectedKey={intent} onSelectionChange={(k) => setIntent(String(k) as TeamworkIntent)} className="me-auto w-fit">
              <Segment.Item id="reply">Reply</Segment.Item>
              <Segment.Item id="steer">Steer</Segment.Item>
            </Segment>
          ) : null}
          <Button size="sm" variant="primary" isDisabled={!text.trim() || busy} onPress={() => void send()}>
            {busy ? 'Sending…' : steer && intent === 'steer' ? 'Send steer' : 'Send reply'}
          </Button>
        </div>
      </ListBlock>
    </ListGroup>
  );
}

/** Who steers the session: its owner, and the people the owner chose. Only the owner changes the list. */
function Steerers({ projectId, rec, me, members, nameOf, onChange }: { projectId: string; rec: ConversationRecord; me?: string; members: ProjectMember[]; nameOf: (id: string) => string; onChange: () => void }) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const owner = rec.data.owner;
  const list = rec.data.steerers ?? [];
  const isOwner = Boolean(owner && owner.id === me);
  const save = async (next: string[]) => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.setTeamworkSteerers(projectId, rec.id, next);
      onChange();
    } catch (e) {
      setError(e instanceof ApiError ? (refusalWords(e.body, nameOf) ?? e.message) : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!owner) return null;
  const candidates = members.filter((m) => m.subjectId !== owner.id && !list.includes(m.subjectId));
  return (
    <ListGroup title="Who steers" count={list.length + 1} description={isOwner ? 'You started this session; you choose who else may steer it.' : 'Only the person who started the session changes this.'}>
      <ListRow icon={<Person />} title={owner.id === me ? 'You' : owner.name} value="Started it" />
      {list.map((id) => (
        <ListRow
          key={id}
          icon={<Person />}
          title={id === me ? 'You' : nameOf(id)}
          {...(isOwner
            ? {
                control: (
                  <Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void save(list.filter((x) => x !== id))}>
                    Remove
                  </Button>
                ),
                compactControl: true,
              }
            : { value: 'Steers' })}
        />
      ))}
      {isOwner && candidates.length ? (
        <ListRow
          icon={<Persons />}
          title="Let someone else steer"
          description="Someone on the project, who may then steer as you do."
          control={<ChoiceSelect label="Let someone else steer" className="w-full max-w-72" value={NONE} options={{ [NONE]: 'Choose a person…', ...Object.fromEntries(candidates.map((m) => [m.subjectId, m.name ?? m.email ?? 'Someone on the project'])) }} onChange={(v) => v !== NONE && void save([...list, v])} isDisabled={busy} />}
        />
      ) : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
